import { useEffect, useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import { Send, Paperclip, Zap, FileCode, CheckCheck, Check, Clock, FileText, Download, X, CircleCheck, Reply, Copy, Search } from "lucide-react";
import api, { errText, fileUrl } from "@/lib/api";
import { subscribeRealtime } from "@/lib/realtime";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const StatusIcon = ({ status }) => {
  if (status === "read") return <CheckCheck size={13} className="text-blue-500" />;
  if (status === "delivered") return <CheckCheck size={13} className="text-slate-500" />;
  if (status === "sent") return <Check size={13} className="text-slate-500" />;
  if (status === "failed") return <X size={13} className="text-red-500" />;
  return <Clock size={12} className="text-slate-400" />;
};

const MediaContent = ({ msg, onPreview }) => {
  const m = msg.media;
  if (!m || m.error) return <div className="text-xs italic text-slate-500">Media unavailable</div>;
  const url = fileUrl(m.file_id);
  if (msg.type === "image" || msg.type === "sticker")
    return <img src={url} alt={m.filename} className="rounded-lg max-w-[240px] max-h-[260px] object-cover cursor-pointer border" style={{ borderColor: "var(--cd-navy)" }} onClick={() => onPreview(url)} data-testid="chat-image" />;
  if (msg.type === "video")
    return <video src={url} controls className="rounded-lg max-w-[260px] border" style={{ borderColor: "var(--cd-navy)" }} data-testid="chat-video" />;
  if (msg.type === "audio")
    return <audio src={url} controls className="max-w-[240px]" data-testid="chat-audio" />;
  return (
    <a href={url} target="_blank" rel="noreferrer" className="flex items-center gap-2 bg-white/70 rounded-lg px-3 py-2 border hover:bg-white" style={{ borderColor: "var(--cd-navy)" }} data-testid="chat-document">
      <FileText size={18} />
      <span className="text-xs font-semibold truncate max-w-[150px]">{m.filename || "Document"}</span>
      <Download size={14} />
    </a>
  );
};

const windowRemaining = (conv) => {
  if (!conv.last_inbound_at) return null;
  const closes = new Date(conv.last_inbound_at).getTime() + 24 * 3600 * 1000;
  const left = closes - Date.now();
  if (left <= 0) return null;
  const h = Math.floor(left / 3600000);
  const m = Math.floor((left % 3600000) / 60000);
  return `${h}h ${m}m`;
};

const MsgActions = ({ m, canReply, onReply, onCopy }) => (
  <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5 self-center mx-1 shrink-0">
    {canReply && m.wa_message_id && (
      <button className="btn-ghost2 p-1" title="Reply to this message" data-testid={`reply-btn-${m.id}`} onClick={() => onReply(m)}>
        <Reply size={14} />
      </button>
    )}
    {m.text && (
      <button className="btn-ghost2 p-1" title="Copy text" data-testid={`copy-btn-${m.id}`} onClick={() => onCopy(m.text)}>
        <Copy size={13} />
      </button>
    )}
  </div>
);

export default function ChatPane({ conv, onConvUpdate }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [convLive, setConvLive] = useState(conv);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [quickReplies, setQuickReplies] = useState([]);
  const [showQR, setShowQR] = useState(false);
  const [qrSearch, setQrSearch] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [tplOpen, setTplOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [tplSel, setTplSel] = useState(null);
  const [tplParams, setTplParams] = useState([]);
  const [tplHeaderUrl, setTplHeaderUrl] = useState("");
  const [agents, setAgents] = useState([]);
  const bottomRef = useRef(null);
  const fileRef = useRef(null);
  const firstLoad = useRef(true);
  const typingRef = useRef(0);

  const signalTyping = () => {
    if (!convLive?.window_open) return;
    const now = Date.now();
    if (now - typingRef.current < 20000) return;
    typingRef.current = now;
    api.post(`/conversations/${conv.id}/typing`).catch(() => {});
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/conversations/${conv.id}/messages`);
      setMessages(data.messages);
      setConvLive(data.conversation);
    } catch (e) {}
  }, [conv.id]);

  useEffect(() => {
    load();
    const unsub = subscribeRealtime((ev) => {
      if ((ev.type === "message" || ev.type === "conversations") && (!ev.conversation_id || ev.conversation_id === conv.id)) load();
    });
    const t = setInterval(load, 15000);
    api.get("/quick-replies").then((r) => setQuickReplies(r.data)).catch(() => {});
    if (user?.role === "admin") api.get("/users").then((r) => setAgents(r.data.filter((u) => u.active))).catch(() => {});
    return () => { clearInterval(t); unsub(); };
  }, [load, user, conv.id]);

  useEffect(() => {
    if (messages.length) {
      bottomRef.current?.scrollIntoView({ behavior: firstLoad.current ? "auto" : "smooth" });
      firstLoad.current = false;
    }
  }, [messages.length]);

  const openTemplates = async () => {
    setTplOpen(true);
    try {
      const { data } = await api.get("/templates");
      setTemplates(data.filter((t) => t.status === "APPROVED"));
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const paramCount = (tpl) => {
    const body = (tpl?.components || []).find((c) => c.type === "BODY");
    const matches = (body?.text || "").match(/\{\{\d+\}\}/g);
    return matches ? matches.length : 0;
  };
  const hasImageHeader = (tpl) => (tpl?.components || []).some((c) => c.type === "HEADER" && c.format === "IMAGE");

  const sendText = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/conversations/${conv.id}/messages`, { text, reply_to: replyTo?.id || null });
      setText("");
      setReplyTo(null);
      await load();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const copyMsg = async (t) => {
    try {
      await navigator.clipboard.writeText(t);
    } catch (e) {
      const ta = document.createElement("textarea");
      ta.value = t;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    toast.success("Copied to clipboard");
  };

  const onPaste = (e) => {
    const items = e.clipboardData?.items || [];
    for (const it of items) {
      if (it.kind === "file") {
        const f = it.getAsFile();
        if (f) {
          e.preventDefault();
          sendFile(f);
          return;
        }
      }
    }
  };

  const sendFile = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("caption", text);
    try {
      await api.post(`/conversations/${conv.id}/media`, fd);
      setText("");
      await load();
      toast.success("Media sent");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const sendTemplate = async () => {
    if (!tplSel) return;
    setBusy(true);
    try {
      await api.post(`/conversations/${conv.id}/send-template`, {
        template_name: tplSel.name,
        language: tplSel.language,
        body_params: tplParams,
        header_image_url: tplHeaderUrl || null,
      });
      setTplOpen(false);
      setTplSel(null);
      await load();
      toast.success("Template sent");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const sendQuickReplyDirect = async (qrId) => {
    setBusy(true);
    try {
      await api.post(`/conversations/${conv.id}/quick-reply/${qrId}`);
      await load();
      toast.success("Quick message sent");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const updateConv = async (patch) => {
    try {
      await api.patch(`/conversations/${conv.id}`, patch);
      await load();
      onConvUpdate();
      toast.success("Updated");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendText();
    }
    if (e.key === "/" && text === "") setShowQR(true);
    else if (e.key === "Escape") setShowQR(false);
  };

  const [dragOver, setDragOver] = useState(false);

  const onDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    const productData = e.dataTransfer.getData("application/x-cd-product");
    if (productData) {
      try {
        const { file_id, caption } = JSON.parse(productData);
        setBusy(true);
        await api.post(`/conversations/${conv.id}/send-file`, { file_id, caption });
        await load();
        toast.success("Product photo sent");
      } catch (err) {
        toast.error(errText(err));
      } finally {
        setBusy(false);
      }
      return;
    }
    if (e.dataTransfer.files?.length) sendFile(e.dataTransfer.files[0]);
  };

  const remaining = windowRemaining(convLive);
  const isOpen = !!remaining;

  return (
    <div
      className="flex-1 min-w-0 flex flex-col chat-bg relative"
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); }}
      onDrop={onDrop}
      data-testid="chat-drop-zone"
    >
      {dragOver && (
        <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none" style={{ background: "rgba(37,211,102,0.12)", border: "3px dashed #25D366" }} data-testid="drop-overlay">
          <div className="card-tactile card-green px-6 py-3 font-heading font-bold">Drop to send in chat 📤</div>
        </div>
      )}
      <div className="px-4 py-2.5 border-b-2 bg-white/80 backdrop-blur-sm flex items-center justify-between gap-3" style={{ borderColor: "var(--cd-navy)" }}>
        <div className="min-w-0">
          <div className="font-heading font-bold truncate" data-testid="chat-contact-name">{convLive.contact_name || convLive.phone}</div>
          <div className="text-xs text-slate-500 font-mono2">+{convLive.phone}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {user?.role === "admin" && (
            <select
              data-testid="assign-agent-select"
              className="input-tactile py-1 text-xs w-36"
              value={convLive.assigned_to || ""}
              onChange={(e) => updateConv({ assigned_to: e.target.value })}
            >
              <option value="">Unassigned</option>
              {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          )}
          {convLive.status !== "resolved" ? (
            <button data-testid="resolve-btn" className="btn-yellow py-1.5 text-xs" onClick={() => updateConv({ status: "resolved" })}>
              <CircleCheck size={14} /> Resolve
            </button>
          ) : (
            <button data-testid="reopen-btn" className="btn-navy py-1.5 text-xs" onClick={() => updateConv({ status: "open" })}>Reopen</button>
          )}
        </div>
      </div>

      <div className="px-4 py-1.5 text-xs font-semibold flex items-center gap-2 border-b" style={{ background: isOpen ? "var(--cd-green)" : "var(--cd-pink)", borderColor: "var(--cd-navy)" }} data-testid="session-window-banner">
        <Clock size={12} />
        {isOpen ? `${remaining} left in the 24h Meta session window — free-form replies allowed` : "24h window closed — only approved templates can be sent"}
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2" data-testid="chat-messages">
        {messages.map((m) =>
          m.type === "system" ? (
            <div key={m.id} className="flex justify-center">
              <span className="font-hand text-sm text-amber-800 bg-white/70 px-3 py-0.5 rounded-full border" style={{ borderColor: "#E5DCB8" }}>{m.text}</span>
            </div>
          ) : (
            <div key={m.id} className={`flex items-center ${m.direction === "outbound" ? "justify-end" : "justify-start"} fade-up group`}>
              {m.direction === "outbound" && <MsgActions m={m} canReply={isOpen} onReply={setReplyTo} onCopy={copyMsg} />}
              <div className={`max-w-[70%] px-3 py-2 select-text ${m.direction === "outbound" ? "bubble-out" : "bubble-in"}`}>
                {m.reply_to && (
                  <div className="border-l-4 rounded px-2 py-1 mb-1 text-xs bg-black/5" style={{ borderColor: "#25D366" }} data-testid={`quoted-${m.id}`}>
                    <div className="font-bold text-[10px] text-emerald-700">
                      {m.reply_to.direction === "inbound" ? (convLive.contact_name || "Customer") : (m.reply_to.sender_name || "You")}
                    </div>
                    <div className="truncate text-slate-600 max-w-[220px]">{m.reply_to.text || `[${m.reply_to.type}]`}</div>
                  </div>
                )}
                {m.type === "template" && (
                  <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 flex items-center gap-1 mb-0.5"><FileCode size={11} /> {m.template_name}</div>
                )}
                {m.media && <div className="mb-1"><MediaContent msg={m} onPreview={setPreview} /></div>}
                {m.text && <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>}
                <div className="flex items-center gap-1 justify-end mt-0.5">
                  {m.sender_name && <span className="text-[9px] text-slate-500 font-semibold">{m.sender_name}</span>}
                  <span className="text-[9px] text-slate-500">{new Date(m.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                  {m.direction === "outbound" && <StatusIcon status={m.status} />}
                </div>
                {m.status === "failed" && (
                  <div className="text-[10px] text-red-600 font-semibold mt-0.5 flex items-center gap-1" data-testid={`msg-error-${m.id}`} title={m.error || "Delivery failed"}>
                    <X size={10} /> {m.error ? `Failed — ${m.error}` : "Delivery failed (reason not reported)"}
                  </div>
                )}
              </div>
              {m.direction !== "outbound" && <MsgActions m={m} canReply={isOpen} onReply={setReplyTo} onCopy={copyMsg} />}
            </div>
          )
        )}
        <div ref={bottomRef} />
      </div>

      <div className="p-3 border-t-2 bg-white/80 backdrop-blur-sm relative" style={{ borderColor: "var(--cd-navy)" }}>
        {showQR && quickReplies.length > 0 && (
          <div className="absolute bottom-full left-3 mb-1 w-80 card-tactile p-2 max-h-64 overflow-y-auto pop-in z-10" data-testid="quick-replies-popover">
            <div className="label-upper px-1">Quick Replies</div>
            <div className="relative mb-1.5">
              <Search size={13} className="absolute left-2 top-2 text-slate-400" />
              <input
                data-testid="qr-search-input"
                className="input-tactile pl-7 py-1 text-xs"
                placeholder="Search quick replies..."
                value={qrSearch}
                autoFocus
                onChange={(e) => setQrSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Escape") { setShowQR(false); setQrSearch(""); } }}
              />
            </div>
            {quickReplies.filter((q) => !qrSearch || q.shortcut.toLowerCase().includes(qrSearch.toLowerCase()) || (q.text || "").toLowerCase().includes(qrSearch.toLowerCase())).map((q) => (
              <button key={q.id} data-testid={`quick-reply-${q.shortcut}`} className="w-full text-left px-2 py-1.5 rounded hover:bg-[var(--cd-yellow)] text-sm flex items-center gap-1.5" onClick={() => { if (q.media) { sendQuickReplyDirect(q.id); } else { setText(q.text); } setShowQR(false); setQrSearch(""); }}>
                {q.media && (
                  q.media.mime?.startsWith("image/")
                    ? <img src={fileUrl(q.media.file_id)} alt="" className="w-7 h-7 rounded object-cover border shrink-0" style={{ borderColor: "var(--cd-navy)" }} />
                    : <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-[var(--cd-blue)] border shrink-0" style={{ borderColor: "var(--cd-navy)" }}>{q.media.mime?.startsWith("video/") ? "VIDEO" : "FILE"}</span>
                )}
                <span className="min-w-0 truncate"><span className="font-bold font-mono2 text-xs">/{q.shortcut}</span> — <span className="text-slate-600">{q.text.slice(0, 50)}</span></span>
                {q.media && <span className="ml-auto text-[9px] font-bold text-emerald-700 shrink-0">sends now ➤</span>}
              </button>
            ))}
            {quickReplies.filter((q) => !qrSearch || q.shortcut.toLowerCase().includes(qrSearch.toLowerCase()) || (q.text || "").toLowerCase().includes(qrSearch.toLowerCase())).length === 0 && (
              <div className="px-2 py-2 font-hand text-sm text-amber-800" data-testid="qr-no-results">no quick replies match "{qrSearch}"</div>
            )}
          </div>
        )}
        {replyTo && (
          <div className="flex items-center gap-2 mb-2 bg-white border-2 rounded-lg px-2 py-1.5 text-xs" style={{ borderColor: "var(--cd-navy)" }} data-testid="reply-preview-bar">
            <Reply size={14} className="shrink-0 text-emerald-700" />
            <div className="min-w-0">
              <div className="font-bold text-[10px] text-emerald-700">
                Replying to {replyTo.direction === "inbound" ? (convLive.contact_name || "customer") : (replyTo.sender_name || "yourself")}
              </div>
              <div className="truncate text-slate-600">{replyTo.text || `[${replyTo.type}]`}</div>
            </div>
            <button className="btn-ghost2 py-0.5 ml-auto shrink-0" data-testid="cancel-reply-btn" onClick={() => setReplyTo(null)}><X size={13} /></button>
          </div>
        )}
        <div className="flex items-end gap-2">
          <input ref={fileRef} type="file" className="hidden" data-testid="chat-file-input" onChange={(e) => sendFile(e.target.files?.[0])} accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt" />
          <button data-testid="attach-btn" className="btn-ghost2 border-2" style={{ borderColor: "var(--cd-navy)" }} disabled={!isOpen || busy} onClick={() => fileRef.current?.click()} title={isOpen ? "Attach file" : "Window closed"}>
            <Paperclip size={17} />
          </button>
          <button data-testid="quick-reply-btn" className="btn-ghost2 border-2" style={{ borderColor: "var(--cd-navy)" }} onClick={() => setShowQR((s) => !s)} title="Quick replies">
            <Zap size={17} />
          </button>
          <button data-testid="open-template-btn" className="btn-ghost2 border-2" style={{ borderColor: "var(--cd-navy)" }} onClick={openTemplates} title="Send template">
            <FileCode size={17} />
          </button>
          <textarea
            data-testid="chat-input"
            className="input-tactile resize-none flex-1"
            rows={Math.min(6, Math.max(2, text.split("\n").length + Math.floor(text.length / 90)))}
            placeholder={isOpen ? "Type a message... ( / for quick replies )" : "Window closed — send a template instead"}
            value={text}
            disabled={!isOpen}
            onChange={(e) => { setText(e.target.value); if (e.target.value.trim()) signalTyping(); }}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
          />
          <button data-testid="send-message-btn" className="btn-navy py-2" disabled={!isOpen || busy || !text.trim()} onClick={sendText}>
            <Send size={16} />
          </button>
        </div>
      </div>

      {preview && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-8" onClick={() => setPreview(null)} data-testid="image-preview-overlay">
          <img src={preview} alt="preview" className="max-h-full max-w-full rounded-lg" />
          <button className="absolute top-4 right-4 text-white"><X size={28} /></button>
        </div>
      )}

      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent className="card-tactile border-2 max-h-[85vh] overflow-y-auto" style={{ borderColor: "var(--cd-navy)" }}>
          <DialogHeader><DialogTitle className="font-heading">Send Approved Template</DialogTitle></DialogHeader>
          <label className="label-upper">Template</label>
          <select
            data-testid="template-select"
            className="input-tactile"
            value={tplSel?.name || ""}
            onChange={(e) => {
              const t = templates.find((x) => x.name === e.target.value);
              setTplSel(t || null);
              setTplParams(t ? Array(paramCount(t)).fill("") : []);
              setTplHeaderUrl("");
            }}
          >
            <option value="">Select template...</option>
            {templates.map((t) => <option key={t.id || t.name} value={t.name}>{t.name} ({t.language})</option>)}
          </select>
          {tplSel && (
            <>
              <div className="text-xs bg-[var(--cd-green)] border rounded-lg p-2 whitespace-pre-wrap" style={{ borderColor: "var(--cd-navy)" }}>
                {((tplSel.components || []).find((c) => c.type === "BODY") || {}).text}
              </div>
              {hasImageHeader(tplSel) && (
                <>
                  <label className="label-upper">Header Image URL</label>
                  <input data-testid="template-header-url-input" className="input-tactile" placeholder="https://..." value={tplHeaderUrl} onChange={(e) => setTplHeaderUrl(e.target.value)} />
                </>
              )}
              {tplParams.map((p, i) => (
                <div key={i}>
                  <label className="label-upper">Variable {"{{" + (i + 1) + "}}"}</label>
                  <input data-testid={`template-param-${i}`} className="input-tactile" placeholder="Value or {{name}}" value={p} onChange={(e) => setTplParams((arr) => arr.map((x, j) => (j === i ? e.target.value : x)))} />
                </div>
              ))}
              <button data-testid="send-template-btn" className="btn-navy justify-center mt-1" disabled={busy} onClick={sendTemplate}>
                <Send size={15} /> Send Template
              </button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
